import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const FloatingHelpButton = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const checkForModals = () => {
      // Check for common modal/popup elements
      const modalSelectors = [
        '[role="dialog"]',
        '[role="modal"]',
        '.modal',
        '.popup',
        '.overlay',
        '.backdrop',
        '[data-modal]',
        '[data-popup]',
        // Check for mobile menu
        '.mobile-menu',
        '[data-mobile-menu]',
        // Check for date picker
        '.datepicker',
        '.date-picker',
        '[data-datepicker]',
        // Check for login modal
        '.login-modal',
        '.auth-modal',
        '[data-login]',
        // Check for LoginSignupModal specifically
        'div[class*="fixed inset-0 bg-black bg-opacity-50 z-50"]',
        'div[class*="fixed inset-0 bg-black"]',
        // Check for any element with high z-index that might be a modal
        '[style*="z-index: 4"]',
        '[style*="z-index: 5"]',
        '[style*="z-index: 6"]',
        '[style*="z-index: 7"]',
        '[style*="z-index: 8"]',
        '[style*="z-index: 9"]',
        '[style*="z-index: 10"]',
        '[style*="z-index: 11"]',
        '[style*="z-index: 12"]',
        '[style*="z-index: 13"]',
        '[style*="z-index: 14"]',
        '[style*="z-index: 15"]',
        '[style*="z-index: 16"]',
        '[style*="z-index: 17"]',
        '[style*="z-index: 18"]',
        '[style*="z-index: 19"]',
        '[style*="z-index: 20"]',
        '[style*="z-index: 21"]',
        '[style*="z-index: 22"]',
        '[style*="z-index: 23"]',
        '[style*="z-index: 24"]',
        '[style*="z-index: 25"]',
        '[style*="z-index: 26"]',
        '[style*="z-index: 27"]',
        '[style*="z-index: 28"]',
        '[style*="z-index: 29"]',
        '[style*="z-index: 30"]',
        '[style*="z-index: 31"]',
        '[style*="z-index: 32"]',
        '[style*="z-index: 33"]',
        '[style*="z-index: 34"]',
        '[style*="z-index: 35"]',
        '[style*="z-index: 36"]',
        '[style*="z-index: 37"]',
        '[style*="z-index: 38"]',
        '[style*="z-index: 39"]',
        '[style*="z-index: 40"]',
        '[style*="z-index: 41"]',
        '[style*="z-index: 42"]',
        '[style*="z-index: 43"]',
        '[style*="z-index: 44"]',
        '[style*="z-index: 45"]',
        '[style*="z-index: 46"]',
        '[style*="z-index: 47"]',
        '[style*="z-index: 48"]',
        '[style*="z-index: 49"]',
        '[style*="z-index: 50"]'
      ];

      let hasModal = false;
      
      modalSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          const computedStyle = window.getComputedStyle(element);
          const display = computedStyle.display;
          const visibility = computedStyle.visibility;
          const opacity = computedStyle.opacity;
          
          // Check if element is visible and has high z-index
          if (display !== 'none' && visibility !== 'hidden' && opacity !== '0') {
            const zIndex = parseInt(computedStyle.zIndex) || 0;
            if (zIndex >= 50) { // Same or higher than our button's z-50
              hasModal = true;
            }
          }
        });
      });

      // Also check for body overflow hidden (common modal pattern)
      const bodyOverflow = document.body.style.overflow;
      if (bodyOverflow === 'hidden') {
        hasModal = true;
      }

      // Check for any fixed positioned elements that might be modals
      const fixedElements = document.querySelectorAll('[style*="position: fixed"]');
      fixedElements.forEach(element => {
        const computedStyle = window.getComputedStyle(element);
        const zIndex = parseInt(computedStyle.zIndex) || 0;
        if (zIndex >= 50) {
          hasModal = true;
        }
      });

      // Additional check for LoginSignupModal by looking for the specific class combination
      const loginModal = document.querySelector('div.fixed.inset-0.bg-black.bg-opacity-50.z-50');
      if (loginModal) {
        hasModal = true;
      }

      setIsVisible(!hasModal);
    };

    // Check initially
    checkForModals();

    // Set up observers for DOM changes
    const observer = new MutationObserver(checkForModals);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'data-modal', 'data-popup']
    });

    // Also check on scroll and resize
    window.addEventListener('scroll', checkForModals);
    window.addEventListener('resize', checkForModals);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', checkForModals);
      window.removeEventListener('resize', checkForModals);
    };
  }, []);

  const handleHelpClick = () => {
    navigate('/help');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-[15vh] right-[20px] z-40">
      <button 
        onClick={handleHelpClick}
        className="flex items-center bg-white rounded-full p-[5px] gap-[8px] shadow-md border-[1px] border-[#dddddd] hover:shadow-sm transition-all duration-200 group"
      >
        {/* Dog Icon Circle */}
        <div className="w-[38px] h-[38px]  rounded-full flex items-center justify-center relative overflow-hidden">
          {/* Dog GIF */}
          <img 
            src="/assets/dog-help.gif" 
            alt="Help Dog" 
            className="h-[38px] object-cover"
            onError={(e) => {
              // Fallback to SVG if GIF doesn't load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
        </div>
        
        {/* Text Section */}
        <div className="text-left mr-[4px]">
          <div className="text-[#7e7e7e] text-[10px] font-semibold leading-tight">Need</div>
          <div className="text-black text-[10px] font-semibold leading-tight">Help?</div>
        </div>
      </button>
    </div>
  );
};

export default FloatingHelpButton;
